import {AppTranslationFunction} from '@mybake/ui/hooks/useAppTranslation';
import {ApiFieldError, ApiProblem} from '../models/common/ApiProblem';

type QueryError = {status?: number | string; data?: unknown; error?: string};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const statusOf = (error: unknown) => {
    if (!isRecord(error)) return 0;
    const status = (error as QueryError).status;
    return typeof status === 'number' ? status : 0;
};

export const normalizeApiError = (error: unknown): ApiProblem => {
    const queryError = isRecord(error) ? error as QueryError : undefined;
    const data = queryError?.data;

    if (isRecord(data) && typeof data.code === 'string') {
        return {
            type: typeof data.type === 'string' ? data.type : '',
            title: typeof data.title === 'string' ? data.title : 'Request failed',
            status: typeof data.status === 'number' ? data.status : statusOf(error),
            detail: typeof data.detail === 'string' ? data.detail : '',
            instance: typeof data.instance === 'string' ? data.instance : '',
            code: data.code,
            traceId: typeof data.traceId === 'string' ? data.traceId : '',
            errors: isRecord(data.errors) ? data.errors as Record<string, ApiFieldError[]> : null,
            args: isRecord(data.args) ? data.args : null,
        };
    }

    const transportCode = queryError?.status === 'FETCH_ERROR'
        ? 'network.unavailable'
        : queryError?.status === 'TIMEOUT_ERROR'
          ? 'network.timeout'
          : queryError?.status === 'PARSING_ERROR'
            ? 'response.invalid'
            : null;

    return {
        status: statusOf(error),
        code: transportCode ?? 'server.internal_error',
        detail: '',
        title: 'Request failed',
        type: '',
        instance: '',
        traceId: '',
        errors: null,
        args: null,
    };
};

const translateCode = (code: string, args: Record<string, unknown> | null | undefined, t: AppTranslationFunction) => {
    const key = `apiErrors:${code}`;
    const translated = String(t(key as never, (args ?? {}) as never));
    return translated === key || translated === code ? null : translated;
};

const translateItemDeleteConflict = (args: Record<string, unknown> | null | undefined, t: AppTranslationFunction) => {
    const base = translateCode('item.delete.conflict', null, t);
    if (!base) return null;

    const references = (['recipes', 'fillings', 'decorations'] as const).flatMap(kind => {
        const names = args?.[kind];
        if (!Array.isArray(names) || names.length === 0) return [];
        const key = `apiErrors:item.delete.conflict_${kind}`;
        const translated = String(t(key as never, {names} as never));
        return translated === key ? [] : [translated];
    });

    return references.length ? `${base}\n${references.join('\n')}` : base;
};

export const getApiErrorMessage = (error: unknown, t: AppTranslationFunction) => {
    const problem = normalizeApiError(error);
    const conflictOrders = problem.args?.orders;
    const customerConflictOrders = problem.args?.orderNumbers;
    const translationArgs = problem.code === 'customer.active_orders' && Array.isArray(customerConflictOrders)
        ? {...problem.args, orderNumbers: customerConflictOrders.join(', ')}
        : (problem.code === 'recipe.delete.conflict' || problem.code === 'filling.delete.conflict') && Array.isArray(conflictOrders)
        ? {...problem.args, orderNumbers: conflictOrders.join(', ')}
        : problem.args;
    const translated = problem.code === 'item.delete.conflict'
        ? translateItemDeleteConflict(problem.args, t)
        : translateCode(problem.code, translationArgs, t);
    if (translated) return translated;
    // Never expose a backend's English fallback for unexpected server errors.
    // The trace id remains available separately for support diagnostics.
    if (problem.status >= 500) {
        return translateCode('server.internal_error', null, t) || String(t('apiErrors:unknown' as never));
    }
    return problem.detail || String(t('apiErrors:unknown' as never));
};

export const getApiFieldErrors = (error: unknown, t: AppTranslationFunction): Record<string, string> => {
    const problem = normalizeApiError(error);
    return Object.fromEntries(Object.entries(problem.errors ?? {}).flatMap(([field, errors]) => {
        const first = errors?.[0];
        if (!first) return [];
        return [[field, translateCode(first.code, {...(problem.args ?? {}), ...(first.args ?? {})}, t) || first.detail || first.message || String(t('apiErrors:unknown' as never))]];
    }));
};

export const getApiTraceId = (error: unknown) => normalizeApiError(error).traceId;

export type ApiErrorKind = 'validation' | 'server' | 'authorization' | 'business';

export const getApiErrorKind = (error: unknown): ApiErrorKind => {
    const problem = normalizeApiError(error);
    if (problem.code === 'auth.invalid_credentials') return 'validation';
    if (problem.status === 401 || problem.status === 403 || problem.code.startsWith('auth.')) return 'authorization';
    if (problem.errors && Object.keys(problem.errors).length > 0) return 'validation';
    if (
        problem.status >= 500 ||
        problem.code === 'server.internal_error' ||
        problem.code.startsWith('network.') ||
        problem.code === 'response.invalid'
    ) return 'server';
    if (problem.status === 400 || problem.code.startsWith('validation.') || problem.code === 'request.invalid') return 'validation';
    return 'business';
};
